import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { resetForNextCall, showsCallAgain } from 'components/CallScribe/popupCallSession';
import BetweenCallsFrame from 'components/CallScribe/BetweenCallsFrame';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Slide,
} from '@mui/material';
import CallEndIcon from '@mui/icons-material/CallEnd';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import MicNoneIcon from '@mui/icons-material/MicNone';

import { useAppDispatch, useAppSelector } from 'hooks/store';
import { ConfigurationSelector } from 'store/configuration';
import { ThunkInitiateCallScribeCall } from 'store/callScribe/thunks';

import { IUseTwilioDeviceResult } from 'components/CallScribe/useTwilioDevice';
import {
  formattedNumber,
  initiateBodyFor,
  popupLaunchId,
  readPopupCallContext,
  resolveCallAttribution,
} from 'components/CallScribe/popupCallContext';
import PopupLaunchSteps, { type TLaunchStage } from 'components/CallScribe/PopupLaunchSteps';
import { setActiveTranscriptCall } from 'store/callScribe';
import {
  CallScribeAnsweredAtForCallSelector,
  CallScribeParticipantsForCallSelector,
} from 'store/callScribe/selectors';
import { customerDisplayName } from 'services/callScribe/participants';
import { isContactScreen } from 'components/CallScribe/contactScreen';
import { normalizeExternalTicketId } from 'components/CallScribe/ticketId';
import { emitCallEndSignal } from 'components/CallScribe/callEndSignal';
import { useReportCallBusy } from 'components/CallScribe/callBusy';
import { useLeaveGuard } from 'components/CallScribe/useLeaveGuard';
import { useReportLiveCall } from 'components/CallScribe/softphoneLiveCall';
import {
  acquireLiveCallLock,
  askLiveWindowToFocus,
  holderNameFor,
  type ILiveCallLease,
  liveCallHeldElsewhere,
  liveCallHeldMessage,
  readLiveCallHolder,
  releaseLiveCallLock,
  useLiveCallLease,
  watchLiveCallHolder,
  whenLiveCallEnds,
} from 'components/CallScribe/liveCallLease';
import { useHubStarvedFallback } from 'hooks/useHubStarvedFallback';
import { extractApiErrorMessage } from 'services/callScribe/apiError';
import ConversationSection from 'components/CallScribe/ConversationSection';
import PostCallReview from 'components/CallScribe/PostCallReview';
import CallEndedSteps from 'components/CallScribe/CallEndedSteps';
import {
  CLOSE_CONFIRM_CLOSE,
  CLOSE_CONFIRM_KEEP,
  CLOSE_CONFIRM_TITLE,
  closeConfirmBody,
  type TUnsavedWork,
} from 'components/CallScribe/closeConfirm';
import CallIdentity, { CallIdentityTone } from 'components/CallScribe/Dialer/CallIdentity';
import CallTopLine from 'components/CallScribe/Dialer/CallTopLine';
import InCallControls from 'components/CallScribe/Dialer/InCallControls';
import Keypad from 'components/CallScribe/Dialer/Keypad';
import AddPersonOverlay from 'components/CallScribe/Conference/AddPersonOverlay';
import PeopleStrip from 'components/CallScribe/Conference/PeopleStrip';
import { useCallMeta } from 'components/CallScribe/Conference/useCallMeta';
import { formatPhoneE164ToDisplay, parseUtcInstant } from 'components/CallScribe/Recents/recentsHelpers';
import usePopupCallViewStyles from 'components/CallScribe/popupCallViewStyles';
import { useRecordingStop } from 'components/CallScribe/useRecordingStop';

interface IPopupCallViewProps {
  twilio: IUseTwilioDeviceResult;
  launchedByPod?: boolean;
  signedInAs?: string;
}

const DIALED_FLAG_PREFIX = 'mspp.callscribe.windowDialed.';

const hasWindowDialed = (key: string): boolean => {
  try {
    return window.sessionStorage.getItem(DIALED_FLAG_PREFIX + key) !== null;
  } catch {
    return false;
  }
};

const markWindowDialed = (key: string): void => {
  try {
    window.sessionStorage.setItem(DIALED_FLAG_PREFIX + key, String(Date.now()));
  } catch {
    return undefined;
  }
};

type TLiveCallGuard = 'checking' | 'clear' | 'held' | 'otherEnded';

const launchStageFor = (state: IUseTwilioDeviceResult['state']): TLaunchStage => {
  if (state === 'connecting' || state === 'in-call') return 'ringing';
  return state === 'ready' ? 'calling' : 'connecting';
};

const PopupCallView: React.FC<IPopupCallViewProps> = ({
  twilio,
  launchedByPod = false,
  signedInAs,
}) => {
  const classes = usePopupCallViewStyles();
  const dispatch = useAppDispatch();
  const config = useAppSelector(ConfigurationSelector);
  const context = useMemo(
    () => readPopupCallContext(window.location.search, {
      crmSourceId: config.psaId ?? null,
      externalTicketId: isContactScreen(config) ? null : normalizeExternalTicketId(config.ticketId),
    }),
    [config],
  );
  const target = context.toNumber;
  const dialKey = popupLaunchId(window.location.search) ?? target;

  const [reviewUnsaved, setReviewUnsaved] = useState<TUnsavedWork | null>(null);
  const [askingToClose, setAskingToClose] = useState(false);
  const closingRef = useRef(false);

  const requestClose = (): void => {
    if (askingToClose) return;
    if (reviewUnsaved !== null) {
      setAskingToClose(true);
      return;
    }
    window.close();
  };

  const closeNow = (): void => {
    closingRef.current = true;
    window.close();
  };

  useEffect(() => {
    if (reviewUnsaved === null) return undefined;
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      if (closingRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => { window.removeEventListener('beforeunload', onBeforeUnload); };
  }, [reviewUnsaved]);

  const startedRef = useRef(false);
  const wasActiveRef = useRef(false);
  const endSignaledRef = useRef(false);
  const [startError, setStartError] = useState<string | undefined>(undefined);
  const [alreadyPlaced, setAlreadyPlaced] = useState(false);
  const [awaitingClick, setAwaitingClick] = useState(false);
  const [activeCallId, setActiveCallId] = useState<number | undefined>(undefined);
  const [documentationNotice, setDocumentationNotice] = useState<string | undefined>(undefined);
  const [redialBusy, setRedialBusy] = useState(false);
  const [otherNumber, setOtherNumber] = useState('');
  const [dialledNumber, setDialledNumber] = useState(target);
  const [guard, setGuard] = useState<TLiveCallGuard>(() => (
    target && !hasWindowDialed(dialKey) ? 'checking' : 'clear'
  ));
  const [heldBy, setHeldBy] = useState<ILiveCallLease | null>(null);
  const [learnedName, setLearnedName] = useState<string | null>(null);

  const noteHolder = (holder: ILiveCallLease | null): void => {
    setHeldBy(holder);
    const name = holderNameFor(holder, target);
    if (name) setLearnedName(name);
  };

  const holdForOtherWindow = (): void => {
    noteHolder(readLiveCallHolder());
    setGuard('held');
    askLiveWindowToFocus();
  };

  const launchCallee = context.contactName || learnedName || formattedNumber(target);

  useEffect(() => {
    if (twilio.state === 'connecting' || twilio.state === 'in-call') {
      wasActiveRef.current = true;
    }
  }, [twilio.state]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (cancelled) {
          stream.getTracks().forEach(t => { t.stop(); });
          return;
        }
        stream.getTracks().forEach(t => { t.stop(); });
      } catch {
        return undefined;
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (guard !== 'checking') return undefined;
    let cancelled = false;
    const blocked = launchedByPod
      ? acquireLiveCallLock({ callId: null, callee: launchCallee, toNumber: target }).then(ok => !ok)
      : liveCallHeldElsewhere();
    void blocked.then(isBlocked => {
      if (cancelled) return;
      if (isBlocked) holdForOtherWindow();
      else setGuard('clear');
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (guard !== 'held') return undefined;
    const stopWatching = watchLiveCallHolder(noteHolder);
    const stopWaiting = whenLiveCallEnds(() => { setGuard('otherEnded'); });
    return () => {
      stopWatching();
      stopWaiting();
    };
  }, [guard]);

  useEffect(() => {
    if (startedRef.current) return;
    if (!target) {
      setStartError('No phone number was provided.');
      startedRef.current = true;
      return;
    }
    if (guard === 'checking') return;
    if (guard !== 'clear') {
      startedRef.current = true;
      return;
    }
    if (twilio.state !== 'ready') return;

    startedRef.current = true;

    if (hasWindowDialed(dialKey)) {
      setAlreadyPlaced(true);
      return;
    }

    if (!launchedByPod) {
      setAwaitingClick(true);
      return;
    }

    void dialOwnCallRef.current();
  }, [twilio.state, target, twilio, guard]);

  useEffect(() => {
    if (typeof activeCallId !== 'number') return;
    dispatch(setActiveTranscriptCall(activeCallId));
  }, [activeCallId, dispatch]);

  const dialExplicitly = async (to: string, sameNumber: boolean): Promise<void> => {
    setRedialBusy(true);
    const callee = sameNumber ? launchCallee : formattedNumber(to);
    if (!(await acquireLiveCallLock({ callId: null, callee, toNumber: to }))) {
      setRedialBusy(false);
      holdForOtherWindow();
      return;
    }
    setGuard('clear');
    setHeldBy(null);
    const next = resetForNextCall({
      started: startedRef.current,
      wasActive: wasActiveRef.current,
      endSignaled: endSignaledRef.current,
      activeCallId,
      startError,
      alreadyPlaced,
      documentationNotice,
    });
    wasActiveRef.current = next.wasActive;
    endSignaledRef.current = next.endSignaled;
    setActiveCallId(next.activeCallId);
    setStartError(next.startError);
    setAlreadyPlaced(next.alreadyPlaced);
    setDocumentationNotice(next.documentationNotice);
    setAwaitingClick(false);
    setDialledNumber(to);

    const attribution = await resolveCallAttribution(
      sameNumber
        ? context
        : {
          ...context,
          toNumber: to,
          contactExternalId: null,
          companyExternalId: null,
          contactName: null,
          resolveContactByPhone: true,
        },
    );

    try {
      const result = await dispatch(
        ThunkInitiateCallScribeCall(initiateBodyFor(attribution)),
      ).unwrap();

      setActiveCallId(result.id);
      setDocumentationNotice(result.documentationUnavailableReason ?? undefined);
      markWindowDialed(dialKey);
      await twilio.placeCall({ to, callId: result.id });
    } catch (err) {
      setStartError(
        extractApiErrorMessage(err, 'The call could not be started. Please try again.'),
      );
    } finally {
      setRedialBusy(false);
    }
  };

  const dialOwnCallRef = useRef<() => Promise<void>>(() => Promise.resolve());
  useLayoutEffect(() => {
    dialOwnCallRef.current = () => dialExplicitly(target, true);
  });

  const retryCall = (): void => {
    if (twilio.errorMessage) twilio.recover();
    void dialExplicitly(target, true);
  };

  const callEnded =
    twilio.state === 'ready' &&
    startedRef.current &&
    wasActiveRef.current &&
    !startError &&
    !alreadyPlaced;

  useEffect(() => {
    if (!callEnded || endSignaledRef.current) return;
    endSignaledRef.current = true;
    emitCallEndSignal(activeCallId);
  }, [callEnded, activeCallId]);

  const endedParticipants = useAppSelector(CallScribeParticipantsForCallSelector(activeCallId));
  const endedWho = customerDisplayName(endedParticipants, launchCallee);

  const callLive = twilio.state === 'connecting' || twilio.state === 'in-call';
  useLeaveGuard(callLive);
  const liveCallee = customerDisplayName(
    endedParticipants,
    dialledNumber === target ? launchCallee : formattedNumber(dialledNumber),
  );
  useReportLiveCall(callLive && dialledNumber ? { toNumber: dialledNumber, callee: liveCallee } : null);
  useLiveCallLease(
    (callLive || redialBusy) && dialledNumber && guard === 'clear'
      ? { callId: activeCallId ?? null, callee: liveCallee, toNumber: dialledNumber }
      : null,
  );

  const callOver = callEnded || startError !== undefined || twilio.state === 'error';
  useEffect(() => {
    if (guard === 'clear' && callOver) releaseLiveCallLock();
  }, [guard, callOver]);

  useReportCallBusy(
    twilio.state === 'connecting' ||
      twilio.state === 'in-call' ||
      (callEnded && typeof activeCallId === 'number'),
  );

  return (
    <div className={classes.root}>
      {guard === 'held' ? (
        <ClosingFrame
          tone="info"
          title={liveCallHeldMessage(heldBy)}
          hint="End that call first."
        />
      ) : guard === 'otherEnded' ? (
        <OtherCallEnded
          callee={launchCallee}
          busy={redialBusy}
          onCall={() => { void dialExplicitly(target, true); }}
        />
      ) : alreadyPlaced ? (
        <BetweenCallsFrame
          target={target}
          otherNumber={otherNumber}
          onOtherNumberChange={setOtherNumber}
          busy={redialBusy}
          canCallAgain={showsCallAgain({
            callEnded: true,
            awaitingAutomaticPost: false,
            hasTarget: Boolean(target),
          })}
          onCallAgain={() => { void dialExplicitly(target, true); }}
          onCallOther={() => { void dialExplicitly(otherNumber, false); }}
        />
      ) : callEnded && typeof activeCallId === 'number' ? (
        <CallEndedSteps
          callId={activeCallId}
          who={endedWho}
          closeLabel="Close"
          onClose={requestClose}
        >
          <PostCallReview
            callId={activeCallId}
            crmSourceId={config.psaId ?? null}
            documentationNotice={documentationNotice}
            onDismiss={requestClose}
            onUnsavedChange={setReviewUnsaved}
          />
        </CallEndedSteps>
      ) : callEnded ? (
        <ClosingFrame
          tone="done"
          title="Call ended"
          hint="This call was not recorded against a ticket, so there is nothing to review."
        />
      ) : awaitingClick ? (
        <ReadyToCall
          callee={launchCallee}
          number={formattedNumber(target)}
          busy={redialBusy}
          onCall={() => { void dialExplicitly(target, true); }}
        />
      ) : (
        <CallStageView
          twilio={twilio}
          target={target}
          startError={startError}
          callId={activeCallId}
          documentationNotice={documentationNotice}
          retrying={redialBusy}
          onRetry={retryCall}
          steps={launchedByPod ? (
            <PopupLaunchSteps
              stage={launchStageFor(twilio.state)}
              signedInAs={signedInAs}
              callee={launchCallee}
              number={formattedNumber(target)}
            />
          ) : undefined}
        />
      )}

      <Dialog open={askingToClose} onClose={() => { setAskingToClose(false); }}>
        <DialogTitle>{CLOSE_CONFIRM_TITLE}</DialogTitle>
        <DialogContent>{closeConfirmBody(reviewUnsaved ?? 'notes')}</DialogContent>
        <DialogActions>
          <Button onClick={() => { setAskingToClose(false); }}>{CLOSE_CONFIRM_KEEP}</Button>
          <Button variant="contained" onClick={closeNow}>{CLOSE_CONFIRM_CLOSE}</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export const ReadyToCall: React.FC<{
  callee: string;
  number: string;
  busy: boolean;
  onCall: () => void;
}> = ({ callee, number, busy, onCall }) => {
  const classes = usePopupCallViewStyles();
  return (
    <div className={classes.stage}>
      <CallIdentity
        name={callee}
        status="Not dialled yet"
        tone="pending"
        inputVolume={0}
        outputVolume={0}
        ringActive={false}
      />
      <Button
        variant="contained"
        disabled={busy}
        className={classes.actionButton}
        onClick={onCall}
      >
        {busy ? 'Calling…' : `Call ${number}`}
      </Button>
      <Button
        variant="text"
        className={classes.quietButton}
        onClick={() => { window.close(); }}
      >
        Close
      </Button>
    </div>
  );
};

const OtherCallEnded: React.FC<{
  callee: string;
  busy: boolean;
  onCall: () => void;
}> = ({ callee, busy, onCall }) => {
  const classes = usePopupCallViewStyles();
  return (
    <div className={classes.endedCard}>
      <div className={classes.endedGlyph}>
        <PhoneInTalkOutlinedIcon fontSize="large" />
      </div>
      <div className={classes.endedTitle} role="status">
        {`Your other call ended. Call ${callee} now?`}
      </div>
      <Button
        variant="contained"
        disabled={busy}
        className={classes.actionButton}
        onClick={onCall}
      >
        {busy ? 'Calling…' : 'Call'}
      </Button>
      <Button
        variant="text"
        className={classes.quietButton}
        onClick={() => { window.close(); }}
      >
        Close
      </Button>
    </div>
  );
};

export const ClosingFrame: React.FC<{
  tone: 'done' | 'error' | 'info';
  title: string;
  hint: string;
  hideClose?: boolean;
}> = ({ tone, title, hint, hideClose = false }) => {
  const classes = usePopupCallViewStyles();
  return (
    <div className={classes.endedCard}>
      <div className={classes.endedGlyph}>
        {tone === 'done' ? <CheckCircleOutlineIcon fontSize="large" /> : null}
        {tone === 'error' ? <ErrorOutlineIcon fontSize="large" /> : null}
        {tone === 'info' ? <PhoneInTalkOutlinedIcon fontSize="large" /> : null}
      </div>
      <div className={classes.endedTitle}>{title}</div>
      <div className={classes.endedHint}>{hint}</div>
      {hideClose ? null : (
        <Button
          variant="contained"
          className={classes.actionButton}
          onClick={() => { window.close(); }}
        >
          Close
        </Button>
      )}
    </div>
  );
};

interface ICallStageViewProps {
  twilio: IUseTwilioDeviceResult;
  target: string;
  startError: string | undefined;
  callId: number | undefined;
  documentationNotice?: string | undefined;
  retrying?: boolean;
  onRetry?: () => void;
  steps?: React.ReactNode;
}

const MICROPHONE_ERROR_CODES = new Set([31201, 31208, 31402]);

export const recoveryFor = (
  message: string,
  code: number | undefined,
): { title: string; fix: string } => {
  if ((code !== undefined && MICROPHONE_ERROR_CODES.has(code)) ||
    /microphone|permission denied|notallowederror/i.test(message)) {
    return {
      title: 'Your microphone is blocked',
      fix: 'Allow microphone access for this site from the icon in the address bar, then choose Try again.',
    };
  }
  return { title: 'The call could not be placed', fix: message };
};

const LiveTranscript = React.memo(ConversationSection);

export const CallStageView: React.FC<ICallStageViewProps> = ({
  twilio,
  target,
  startError,
  callId,
  documentationNotice,
  retrying = false,
  onRetry,
  steps,
}) => {
  const classes = usePopupCallViewStyles();
  const [showKeypad, setShowKeypad] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [dtmf, setDtmf] = useState('');
  const recording = useRecordingStop(callId);
  const answeredAtUtc = useAppSelector(CallScribeAnsweredAtForCallSelector(callId));
  const answeredAtMs = useMemo(() => {
    const parsed = parseUtcInstant(answeredAtUtc);
    return parsed ? parsed.getTime() : undefined;
  }, [answeredAtUtc]);

  const status = (() => {
    if (startError || twilio.state === 'error') return 'Call failed';
    if (twilio.state === 'registering' || twilio.state === 'idle') return 'Connecting softphone…';
    if (twilio.state === 'ready') return 'Starting call…';
    if (twilio.state === 'connecting') return 'Calling…';
    if (twilio.state === 'in-call') {
      return typeof answeredAtMs === 'number' ? 'In call' : 'Calling…';
    }
    return '';
  })();

  const errorText = startError ?? twilio.errorMessage;
  const callActive = twilio.state === 'in-call' || twilio.state === 'connecting';
  useHubStarvedFallback(callId, callActive);
  const inCall = twilio.state === 'in-call';
  const participants = useAppSelector(CallScribeParticipantsForCallSelector(callId));
  const connected = inCall && typeof answeredAtMs === 'number' && typeof callId === 'number';
  const meta = useCallMeta(callId, showAdd);
  const displayName = customerDisplayName(
    participants,
    formatPhoneE164ToDisplay(target) || target || '—',
  );
  const subtitle = formatPhoneE164ToDisplay(target) || target;

  const tone: CallIdentityTone = (() => {
    if (errorText) return 'error';
    if (inCall && typeof answeredAtMs === 'number') return 'live';
    return 'pending';
  })();

  const hideKeypad = (): void => {
    setShowKeypad(false);
    setDtmf('');
  };

  const onDtmfPress = (key: string): void => {
    try {
      twilio.activeCall?.sendDigits(key);
      setDtmf(prev => (prev + key).slice(-24));
    } catch {
      return undefined;
    }
  };

  const recovery = errorText
    ? recoveryFor(errorText, startError ? undefined : twilio.errorCode)
    : null;
  const canRetry = Boolean(recovery && target && onRetry);
  const launching = Boolean(steps) && !recovery;

  const documentationPanel = documentationNotice ? (
    <div className={classes.noticePanel} role="status">
      <span className={classes.noticeTitle}>
        <InfoOutlinedIcon fontSize="small" />
        This call will not be documented on the ticket
      </span>
      <span className={classes.noticeBody}>{documentationNotice}</span>
    </div>
  ) : null;

  const keypadOverlay = (
    <Slide direction="up" in={showKeypad} mountOnEnter unmountOnExit>
      <div className={classes.keypadOverlay} role="dialog" aria-label="Dial pad">
        <div className={classes.dtmfDisplay}>{dtmf || ' '}</div>
        <Keypad onPress={onDtmfPress} variant="onGround" />
        <div className={classes.overlayActions}>
          <div className={classes.overlayEndSlot}>
            <button
              type="button"
              aria-label="End call"
              onClick={twilio.hangup}
              className={`${classes.endButton} ${classes.endButtonSmall}`}
            >
              <CallEndIcon fontSize="medium" />
            </button>
          </div>
          <button
            type="button"
            className={classes.hideButton}
            onClick={hideKeypad}
            aria-label="Hide keypad"
          >
            Hide keypad
          </button>
        </div>
      </div>
    </Slide>
  );

  const addOverlay = typeof callId === 'number' ? (
    <AddPersonOverlay
      open={showAdd && connected}
      callId={callId}
      crmSourceId={meta?.crmSourceId}
      companyId={meta?.companyExternalId}
      onClose={() => { setShowAdd(false); }}
    />
  ) : null;

  const controls = (
    <InCallControls
      variant="onGround"
      isMuted={twilio.isMuted}
      isKeypadOpen={showKeypad}
      onMuteToggle={() => { twilio.mute(!twilio.isMuted); }}
      onKeypadToggle={() => { setShowKeypad(v => !v); }}
      onEndCall={twilio.hangup}
      onAddPerson={typeof callId === 'number' ? () => { setShowAdd(true); } : undefined}
      addDisabled={!connected}
      recordingState={recording.recordingState}
      onStopRecording={recording.onStopRecording}
    />
  );

  if (callActive && !errorText) {
    return (
      <div className={classes.stageActive}>
        <CallTopLine
          recordingState={recording.recordingState}
          onStopRecording={recording.onStopRecording}
        />

        <CallIdentity
          compact
          name={displayName}
          subtitle={subtitle}
          status={status}
          tone={tone}
          answeredAtMs={answeredAtMs}
          inputVolume={twilio.inputVolume}
          outputVolume={twilio.outputVolume}
          ringActive={inCall}
        />

        <PeopleStrip
          callId={callId}
          onAdd={typeof callId === 'number' ? () => { setShowAdd(true); } : undefined}
          addDisabled={!connected}
        />

        {twilio.inputDevices.length > 1 && inCall ? (
          <div className={classes.micRow}>
            <MicNoneIcon fontSize="inherit" />
            <Select
              size="small"
              variant="standard"
              disableUnderline
              className={classes.micSelect}
              value={twilio.selectedInputDeviceId ?? ''}
              onChange={e => { void twilio.setInputDevice(String(e.target.value)); }}
              inputProps={{ 'aria-label': 'Microphone' }}
            >
              {twilio.inputDevices.map(d => (
                <MenuItem key={d.deviceId} value={d.deviceId}>
                  {d.label || `Microphone ${d.deviceId.slice(0, 6)}`}
                </MenuItem>
              ))}
            </Select>
          </div>
        ) : null}

        {documentationPanel}

        {steps && typeof answeredAtMs !== 'number' ? (
          <div className={classes.ringingZone}>{steps}</div>
        ) : (
          <LiveTranscript
            callId={callId}
            live
            variant="stage"
            listening={inCall}
          />
        )}

        <div className={classes.controlsZone}>{controls}</div>

        {keypadOverlay}
        {addOverlay}
      </div>
    );
  }

  return (
    <div className={classes.stage}>
      <CallIdentity
        name={displayName}
        subtitle={subtitle}
        status={status}
        tone={tone}
        answeredAtMs={answeredAtMs}
        inputVolume={twilio.inputVolume}
        outputVolume={twilio.outputVolume}
        ringActive={callActive}
      />

      {documentationPanel}

      {recovery ? (
        <div className={classes.errorPanel} role="alert">
          <span className={classes.errorTitle}>
            <ErrorOutlineIcon fontSize="small" />
            {recovery.title}
          </span>
          <span className={classes.errorBody}>{recovery.fix}</span>
        </div>
      ) : steps ?? null}

      {callActive ? (
        <div className={classes.controlsZone}>{controls}</div>
      ) : (
        <>
          {canRetry ? (
            <Button
              variant="contained"
              disabled={retrying}
              className={classes.actionButton}
              onClick={onRetry}
            >
              {retrying ? 'Calling…' : 'Try again'}
            </Button>
          ) : null}
          <Button
            variant={canRetry || launching ? 'text' : 'contained'}
            className={canRetry || launching ? classes.quietButton : classes.actionButton}
            onClick={() => { window.close(); }}
          >
            {launching ? 'Cancel' : 'Close'}
          </Button>
        </>
      )}

      {callActive ? keypadOverlay : null}
    </div>
  );
};

export default PopupCallView;
